import {
  Badge,
  BlockStack,
  Box,
  Card,
  Divider,
  InlineGrid,
  InlineStack,
  Text,
} from '@shopify/polaris'
import type { BccData } from './useBccData'
import { CardHeader } from './LensBadge'
import { computeCashSafety, STATUS_LABEL, STATUS_TONE } from './cashSafety'

// ── Helpers ───────────────────────────────────────────────────────────────────

function egp(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(2)}M EGP`
  if (n >= 1_000)     return `${(n / 1_000).toFixed(0)}K EGP`
  return `${n.toLocaleString('en-US', { maximumFractionDigits: 0 })} EGP`
}

// ── Metric tile ───────────────────────────────────────────────────────────────

type TileProps = {
  label: string
  value: string
  tone?: 'success' | 'critical'
}

function PositionTile({ label, value, tone }: TileProps) {
  return (
    <Box background="bg-surface-secondary" padding="300" borderRadius="200">
      <BlockStack gap="100">
        <Text as="p" variant="bodySm" tone="subdued">{label}</Text>
        <Text as="p" variant="headingMd"
          tone={tone === 'success' ? 'success' : tone === 'critical' ? 'critical' : undefined}
        >
          {value}
        </Text>
      </BlockStack>
    </Box>
  )
}

// ── Main component ────────────────────────────────────────────────────────────

type Props = { data: BccData }

export function FinancialPanel({ data }: Props) {
  const { dashboard, cash } = data

  // One shared definition, also used by Today's Decisions — see cashSafety.ts
  // for why the old profit-versus-expenses test was structurally wrong.
  const safety = computeCashSafety(data)
  const monthlyExpenses = safety.variableCosts

  const nextSettlement = cash.avgDailySettlement > 0
    ? Math.round(cash.pendingThisMonth / cash.avgDailySettlement)
    : null

  return (
    <BlockStack gap="400">

      {/* Financial Position */}
      <Card>
        <BlockStack gap="400">
          {/* Mixed by nature: COD figures are cash-lens, profit figures are
              performance-lens. Tagged as performance because the P&L numbers
              drive the reading, and maturity qualifies those. */}
          <CardHeader title="Financial Position" lens="performance" maturity={dashboard.maturity} />

          <InlineGrid columns={2} gap="200">
            <PositionTile
              label="COD collected this month"
              value={egp(cash.collectedThisMonth)}
              tone="success"
            />
            <PositionTile
              label="Pending settlement"
              value={egp(cash.pendingThisMonth)}
            />
            <PositionTile
              label="Gross profit"
              value={egp(dashboard.profit.gross)}
            />
            <PositionTile
              label="Variable costs"
              value={egp(monthlyExpenses)}
              tone="critical"
            />
          </InlineGrid>

          <Divider />

          {/* Three profit layers. `profit.net` is operating profit — it subtracts
              only costs that scale with orders. Fixed expenses sit below it and
              produce the real bottom line. See §9.4. */}
          <BlockStack gap="200">
            <InlineStack align="space-between">
              <Text as="p" variant="bodySm" tone="subdued">Operating profit</Text>
              <Text as="p" variant="bodySm" fontWeight="semibold">
                {egp(dashboard.profit.net)}
              </Text>
            </InlineStack>

            <InlineStack align="space-between">
              <Text as="p" variant="bodySm" tone="subdued">
                {`Fixed expenses${dashboard.fixedExpenses.itemCount > 0 ? ` (${dashboard.fixedExpenses.itemCount})` : ''}`}
              </Text>
              <Text as="p" variant="bodySm" fontWeight="semibold" tone="critical">
                {`− ${egp(dashboard.fixedExpenses.total)}`}
              </Text>
            </InlineStack>

            <Divider />

            <InlineStack align="space-between" blockAlign="center">
              <Text as="p" variant="bodyMd" fontWeight="semibold">Net profit</Text>
              <Text as="p" variant="headingMd"
                tone={dashboard.profit.netAfterFixed >= 0 ? 'success' : 'critical'}
              >
                {egp(dashboard.profit.netAfterFixed)}
              </Text>
            </InlineStack>

            {dashboard.fixedExpenses.itemCount === 0 && (
              <Text as="p" variant="bodySm" tone="caution">
                No fixed expenses recorded — rent and payroll are missing, so net profit is overstated.
              </Text>
            )}
          </BlockStack>

        </BlockStack>
      </Card>

      {/* Cash Safety */}
      <Card>
        <BlockStack gap="400">
          <CardHeader title="Cash Safety" lens="cash" />

          <Box
            background={
              safety.status === 'safe'  ? 'bg-surface-success' :
              safety.status === 'tight' ? 'bg-surface-caution' : 'bg-surface-critical'
            }
            padding="400"
            borderRadius="200"
            borderColor={
              safety.status === 'safe'  ? 'border-success' :
              safety.status === 'tight' ? 'border-caution' : 'border-critical'
            }
            borderWidth="025"
          >
            <BlockStack gap="300">
              <InlineStack align="space-between" blockAlign="center">
                <BlockStack gap="100">
                  <Text as="p" variant="bodySm" tone="subdued">Money that must go out</Text>
                  <Text as="p" variant="headingLg">{egp(safety.totalOut)}</Text>
                </BlockStack>
                <BlockStack gap="100" inlineAlign="end">
                  <Badge tone={STATUS_TONE[safety.status]}>{STATUS_LABEL[safety.status]}</Badge>
                  <Text as="p" variant="headingMd"
                    tone={safety.status === 'safe' ? 'success' : safety.status === 'at_risk' ? 'critical' : undefined}
                  >
                    {safety.surplus >= 0
                      ? `+${egp(safety.surplus)} surplus`
                      : `${egp(-safety.surplus)} short`
                    }
                  </Text>
                </BlockStack>
              </InlineStack>

              <Divider />

              {/* The three inputs, shown so the verdict is checkable. */}
              <BlockStack gap="100">
                <InlineStack align="space-between">
                  <Text as="p" variant="bodySm" tone="subdued">Collected so far</Text>
                  <Text as="p" variant="bodySm" fontWeight="semibold">{egp(safety.arrived)}</Text>
                </InlineStack>
                <InlineStack align="space-between">
                  <Text as="p" variant="bodySm" tone="subdued">Awaiting remittance</Text>
                  <Text as="p" variant="bodySm" fontWeight="semibold">{`+ ${egp(safety.expected)}`}</Text>
                </InlineStack>
                <InlineStack align="space-between">
                  <Text as="p" variant="bodySm" tone="subdued">Variable + fixed costs</Text>
                  <Text as="p" variant="bodySm" fontWeight="semibold">{`− ${egp(safety.totalOut)}`}</Text>
                </InlineStack>
              </BlockStack>

              <Text as="p" variant="bodySm">
                {safety.status === 'safe'
                  ? 'Cash already collected covers this period’s outgoings.'
                  : safety.status === 'tight'
                    ? `Collected cash falls short by ${egp(-safety.surplus)}, but ${egp(safety.expected)} awaiting remittance covers it.`
                    : `Even after ${egp(safety.expected)} in outstanding COD arrives, ${egp(-safety.surplusWithExpected)} is still uncovered.`
                }
              </Text>

              {!safety.hasFixedCosts && (
                <Text as="p" variant="bodySm" tone="caution">
                  No fixed expenses recorded — rent and payroll are missing from this check.
                </Text>
              )}
            </BlockStack>
          </Box>

          <Divider />

          <BlockStack gap="200">
            <Text as="h3" variant="headingSm">Working Capital Timeline</Text>

            <InlineStack align="space-between">
              <Text as="p" variant="bodySm" tone="subdued">Next settlement (est.)</Text>
              <Text as="p" variant="bodySm" fontWeight="semibold" tone="success">
                {nextSettlement != null
                  ? `+${egp(cash.pendingThisMonth)} in ~${nextSettlement}d`
                  : `+${egp(cash.pendingThisMonth)} pending`
                }
              </Text>
            </InlineStack>

            <InlineStack align="space-between">
              <Text as="p" variant="bodySm" tone="subdued">Unsettled COD</Text>
              <Text as="p" variant="bodySm" fontWeight="semibold">
                {cash.unsettledOrders} orders · {egp(cash.totalCodAtRisk)}
              </Text>
            </InlineStack>

            {/* Online orders wait on the gateway, not the courier — a separate
                pool with its own clock (§9.8). */}
            {cash.gatewayPendingOrders > 0 && (
              <InlineStack align="space-between">
                <Text as="p" variant="bodySm" tone="subdued">Awaiting gateway payout</Text>
                <Text as="p" variant="bodySm" fontWeight="semibold">
                  {cash.gatewayPendingOrders} orders · {egp(cash.gatewayPendingAmount)}
                </Text>
              </InlineStack>
            )}

            {cash.oldestUnsettledDays > (cash.avgSettlementLagDays ?? 14) * 1.5 && (
              <InlineStack align="space-between">
                <Text as="p" variant="bodySm" tone="subdued">Oldest unsettled</Text>
                <Text as="p" variant="bodySm" fontWeight="semibold" tone="critical">
                  {cash.oldestUnsettledDays} days — follow up with Bosta
                </Text>
              </InlineStack>
            )}

            <InlineStack align="space-between">
              <Text as="p" variant="bodySm" tone="subdued">Avg settlement lag</Text>
              <Text as="p" variant="bodySm" fontWeight="semibold">
                {cash.avgSettlementLagDays != null
                  ? `${cash.avgSettlementLagDays.toFixed(0)} days`
                  : '—'
                }
              </Text>
            </InlineStack>
          </BlockStack>
        </BlockStack>
      </Card>

    </BlockStack>
  )
}
